import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import classNames from 'classnames'
import { type CrossClientQueryExecution, CrossClientQueryResultStatuses } from '../../../../models/CrossClient'
import { useAppSelector } from '../../../../store/hooks'
import { selectCrossClientLandingTeammates, selectCrossClientLandingTeammatesLoading } from '../../../../store/crossClient/crossClientLanding'
import { selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { getUserDisplayName } from '../../../../apps/professional-app/fa/common/aifeatures/utils'
import { formatFriendlyDateTime } from '../../../../common/dateUtils'
import { CrossClientResultSourceIcon } from '../../Results/Sources/CrossClientResultSourceIcon'
import { sortSources } from '../../Results/Sources/utils'
import { onActivateKeyDown } from '../../utils'
import { ReactComponent as ZocksIcon } from '../../../../assets/images/ZocksIcon-primary.svg'
import { ReactComponent as MatchIcon } from '../../../../assets/images/cross-client-match.svg'
import '../History/CrossClientLandingHistory.scss'

interface CrossClientNeedsActionElementProps {
  execution: CrossClientQueryExecution
  variant: 'matches' | 'continue' | 'completed'
}

export const CrossClientNeedsActionElement: React.FC<CrossClientNeedsActionElementProps> = ({ execution, variant }) => {
  const navigate = useNavigate()

  const teammates = useAppSelector(selectCrossClientLandingTeammates)
  const teammatesLoading = useAppSelector(selectCrossClientLandingTeammatesLoading)
  const currentUser = useAppSelector(selectSafeAuthenticatedUser)

  const [hovered, setHovered] = useState<boolean>(false)

  const userName = useMemo(() => {
    return execution.createdBy?.id === currentUser.id
      ? getUserDisplayName(currentUser)
      : getUserDisplayName(teammates.find((t) => t.id === execution.createdBy?.id))
  }, [currentUser, execution.createdBy?.id, teammates])

  const statuses = execution.progress?.statuses ?? {}
  const remaining = statuses[CrossClientQueryResultStatuses.INITIALIZED] ?? 0
  const matchCount = variant === 'completed' ? (execution.matches?.full ?? 0) : (execution.matches?.unactioned ?? 0)

  const fromTab = variant === 'completed' ? 'completed' : 'needs-action'
  const openExecution = () => navigate(`/cross-client/executions/${execution.id}`, { state: { fromTab } })

  return (
    <div
      className={classNames(
        'd-flex zocks-gap-2 p-3 align-items-center border border-1 zocks-border-0.75 bg-white w-100 cross-client-history-element cross-client-transition',
        {
          'zocks-border-color-primary cross-client-history-element-box-shadow': hovered,
          'zocks-border-color-gray-300': !hovered,
        },
      )}
      role='button'
      tabIndex={0}
      onClick={openExecution}
      onKeyDown={onActivateKeyDown(openExecution)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className='d-flex zocks-gap-2 justify-content-between align-items-center w-100'>
        <div className='d-flex flex-column gap-2 flex-grow-1 mw-0'>
          <div className='paragraph-medium medium text-truncate'>{execution.userQuestion}</div>
          <div className='d-flex gap-2 align-items-center paragraph-small zocks-text-gray-700'>
            <div className='d-flex gap-1 align-items-center'>
              <ZocksIcon width='0.875rem' height='0.875rem' />
              {sortSources(execution.sources ?? []).map((source, idx) => (
                <CrossClientResultSourceIcon key={idx} source={source} dimension='0.875rem' />
              ))}
            </div>
            <div className='cross-client-history-meta-separator' />
            <div className='d-flex gap-1 align-items-center text-truncate'>
              <i className='bi bi-calendar' />
              {teammatesLoading && !userName ? (
                <div className='skeleton cross-client-history-name-skeleton' />
              ) : (
                <span className='text-truncate'>
                  {formatFriendlyDateTime(new Date(execution.createdAt))}
                  {userName ? ` by ${userName}` : ''}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className='d-flex gap-1 align-items-center px-2 py-1 border zocks-border-0.5 zocks-border-color-gray-300 cross-client-history-badge flex-shrink-0'>
          {variant === 'continue' ? (
            <>
              <i className='bi bi-play-fill zocks-text-gray-900' />
              <span className='paragraph-small bold zocks-text-gray-900'>
                {remaining} remaining {remaining === 1 ? 'client' : 'clients'}
              </span>
            </>
          ) : (
            <>
              <MatchIcon width={12} height={12} />
              <span className='paragraph-small bold zocks-text-gray-900'>
                {matchCount} {matchCount === 1 ? 'match' : 'matches'}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
